import { FormEvent, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import Navbar from '../components/Navbar'
import { supabase } from '../services/supabaseClient'
import Chatbot from '../components/Chatbot'

export default function BetaSignupPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [invitationCode, setInvitationCode] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isValidatingCode, setIsValidatingCode] = useState(false)
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { signUp } = useAuth()

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (!invitationCode.trim()) {
      setError('Beta invitation code is required')
      return
    }

    setIsLoading(true)

    try {
      let couponId = null
      let isBetaTester = false

      // Validate the beta invitation code
      setIsValidatingCode(true)
      const validateResponse = await fetch('/api/validate-coupon', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code: invitationCode }),
      })

      if (validateResponse.ok) {
        const { isValid, couponId: validatedCouponId } = await validateResponse.json()

        if (isValid) {
          couponId = validatedCouponId
          isBetaTester = true
          console.log('Valid beta code provided:', couponId)
        } else {
          setError('Invalid beta invitation code. Please check your code and try again.')
          setIsValidatingCode(false)
          setIsLoading(false)
          return
        }
      } else {
        setError('Could not validate invitation code. Please try again.')
        setIsValidatingCode(false)
        setIsLoading(false)
        return
      }

      setIsValidatingCode(false)

      // Proceed with beta signup
      console.log('Attempting beta signup');
      const signupResult = await signUp(email, password, {
        coupon_used: couponId,
        is_test_customer: true
      })
      console.log('Signup result:', signupResult);

      // Check if email confirmation is required
      if (!signupResult.session && signupResult.user) {
        // User created but needs email confirmation - redirect to login with success message
        navigate('/login', {
          state: {
            message: 'Beta account created successfully! Please check your email to confirm your account.',
            messageType: 'success'
          }
        })
        return
      }

      // Check for pending transfer
      const pendingTransferToken = sessionStorage.getItem('pendingTransferToken')
      if (pendingTransferToken) {
        sessionStorage.removeItem('pendingTransferToken')
        navigate(`/accept-transfer?token=${pendingTransferToken}`)
      } else {
        // Beta testers get free access, go straight to dashboard
        navigate('/dashboard')
      }
    } catch (err: any) {
      // Show the actual error message to the user
      if (err.message) {
        setError(err.message)
      } else {
        setError('Failed to create an account. Please try again.')
      }
      console.error(err)
    } finally {
      setIsLoading(false)
      setIsValidatingCode(false)
    }
  }

  const handleGoogleSignUp = async () => {
    try {
      setError('')

      if (!invitationCode.trim()) {
        setError('Please enter your beta invitation code first')
        return
      }

      setIsGoogleLoading(true)
      setIsValidatingCode(true)

      // Validate the beta invitation code first
      const validateResponse = await fetch('/api/validate-coupon', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code: invitationCode }),
      })

      if (validateResponse.ok) {
        const { isValid, couponId: validatedCouponId } = await validateResponse.json()

        if (!isValid) {
          setError('Invalid beta invitation code. Please check your code and try again.')
          setIsValidatingCode(false)
          setIsGoogleLoading(false)
          return
        }

        // Store the validated coupon info for use after OAuth callback
        sessionStorage.setItem('betaInvitationCode', invitationCode)
        sessionStorage.setItem('betaCouponId', validatedCouponId)
        sessionStorage.setItem('authReturnUrl', '/dashboard')

        console.log('Beta code validated, redirecting to Google auth')

        const { data, error } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo: `${window.location.origin}/auth/callback`,
            queryParams: {
              access_type: 'offline',
              prompt: 'consent',
            }
          }
        })

        if (error) {
          console.error('Google sign-up error:', error)
          setError(error.message)
          setIsValidatingCode(false)
          setIsGoogleLoading(false)
          return
        }

        // User will be redirected to Google
        console.log('Redirecting to Google authorization page...')
      } else {
        setError('Could not validate invitation code. Please try again.')
        setIsValidatingCode(false)
        setIsGoogleLoading(false)
      }
    } catch (err: any) {
      console.error('Failed to initiate Google sign-up:', err)
      setError('Failed to start Google sign-up. Please try again.')
      setIsValidatingCode(false)
      setIsGoogleLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar isPublic={true} />
      <div className="flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">Join our Beta Program</h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Create your beta account - free access included
          </p>
          <p className="mt-2 text-center text-sm text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500">
              Sign in here
            </Link>
          </p>
          <p className="mt-2 text-center text-sm text-gray-600">
            Don't have a beta code?{' '}
            <Link to="/signup" className="font-medium text-blue-600 hover:text-blue-500">
              Start your free trial instead
            </Link>
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">{error}</h3>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label htmlFor="invitation-code" className="block text-sm font-medium text-gray-700">
                Beta Invitation Code <span className="text-red-500">*</span>
              </label>
              <input
                id="invitation-code"
                name="invitation-code"
                type="text"
                required
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 bg-white rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Enter your beta code"
                value={invitationCode}
                onChange={(e) => setInvitationCode(e.target.value)}
              />
              <p className="mt-1 text-xs text-gray-500">
                Enter the beta invitation code you received to get free access.
              </p>
            </div>
          </div>

          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email-address" className="sr-only">Email address</label>
              <input
                id="email-address"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 bg-white rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">Password</label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="confirm-password" className="sr-only">Confirm Password</label>
              <input
                id="confirm-password"
                name="confirm-password"
                type="password"
                autoComplete="new-password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 bg-white rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Confirm password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading || isValidatingCode || isGoogleLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300"
            >
              {isValidatingCode ? 'Validating code...' : isLoading ? 'Creating beta account...' : 'Create beta account'}
            </button>
          </div>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-gray-50 text-gray-500">Or continue with</span>
              </div>
            </div>

            <div className="mt-6">
              <div className="space-y-3">
                <button
                  type="button"
                  onClick={handleGoogleSignUp}
                  disabled={isLoading || isValidatingCode || isGoogleLoading}
                  className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                >
                  <svg className="h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
                    <g transform="matrix(1, 0, 0, 1, 27.009001, -39.238998)">
                      <path fill="#4285F4" d="M -3.264 51.509 C -3.264 50.719 -3.334 49.969 -3.454 49.239 L -14.754 49.239 L -14.754 53.749 L -8.284 53.749 C -8.574 55.229 -9.424 56.479 -10.684 57.329 L -10.684 60.329 L -6.824 60.329 C -4.564 58.239 -3.264 55.159 -3.264 51.509 Z" />
                      <path fill="#34A853" d="M -14.754 63.239 C -11.514 63.239 -8.804 62.159 -6.824 60.329 L -10.684 57.329 C -11.764 58.049 -13.134 58.489 -14.754 58.489 C -17.884 58.489 -20.534 56.379 -21.484 53.529 L -25.464 53.529 L -25.464 56.619 C -23.494 60.539 -19.444 63.239 -14.754 63.239 Z" />
                      <path fill="#FBBC05" d="M -21.484 53.529 C -21.734 52.809 -21.864 52.039 -21.864 51.239 C -21.864 50.439 -21.724 49.669 -21.484 48.949 L -21.484 45.859 L -25.464 45.859 C -26.284 47.479 -26.754 49.299 -26.754 51.239 C -26.754 53.179 -26.284 54.999 -25.464 56.619 L -21.484 53.529 Z" />
                      <path fill="#EA4335" d="M -14.754 43.989 C -12.984 43.989 -11.404 44.599 -10.154 45.789 L -6.734 42.369 C -8.804 40.429 -11.514 39.239 -14.754 39.239 C -19.444 39.239 -23.494 41.939 -25.464 45.859 L -21.484 48.949 C -20.534 46.099 -17.884 43.989 -14.754 43.989 Z" />
                    </g>
                  </svg>
                  {isGoogleLoading ? 'Connecting...' : 'Sign up with Google'}
                </button>
              </div>
            </div>
          </div>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              By creating an account, you agree to our{' '}
              <Link to="/terms" className="font-medium text-blue-600 hover:text-blue-500">
                Terms of Service
              </Link>
              {' '}and{' '}
              <Link to="/privacy" className="font-medium text-blue-600 hover:text-blue-500">
                Privacy Policy
              </Link>
            </p>
          </div>
        </form>
      </div>
      </div>
      <Chatbot />
    </div>
  )
}
